import { GridFilterItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  buildColumnDefs,
  buildContractOptions,
  buildFilterModel,
  buildInitialColumnSelection,
  buildLocationTypeOptions,
  getDefaultFilterItems,
  LabelValue,
} from './structureListFilters';
import { NodeType } from '../../../@types/structure.type';
import { StructureFilterState } from '../../../components/molecules/StructureFilterBar';
import {
  applyColumnOrder,
  ColumnDef,
  reorderByVisibleIndex,
} from '../../../components/organisms/StructureTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { StructureTypeCategory } from '../../../configs/enums';
import { useColumnRefetchScheduler } from '../../../hooks/useColumnRefetchScheduler';
import { useRefetchLoopGuard } from '../../../hooks/useRefetchLoopGuard';
import { useSessionColumns } from '../../../hooks/useSessionColumns';
import {
  useGetStructurePaginatedDataByType,
  useGetStructuresByTypeCategoryCodeAndTypeCode,
} from '../../../queries/structure-query';
import { useGetStructureTypesByCategoryCode } from '../../../queries/structure-type-query';
import { getVisibleFields } from '../../../utils/columnSelectionOperations';
import { sanitizeSavedReportFilterItems } from '../../../utils/filterModel';
import {
  applySessionColumnState,
  dropColumnWidths,
  readSessionColumnState,
} from '../../../utils/sessionColumnState';

export interface SortState {
  field: string;
  sort: 'asc' | 'desc' | null | undefined;
}

export interface StructureListDataReturn {
  data: Array<Record<string, unknown>>;
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  pageSize: number;
  total: number;
  fetchDurationMs: number | null;
  sortModel: SortState;
  columns: ColumnDef[];
  columnSelectionData: ColumnSelectionItem[];
  filterModel: { items: GridFilterItem[] };
  multiSearchQuery: string;
  locationTypeOptions: LabelValue[];
  contractOptions: LabelValue[];
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSortModel: (model: SortState) => void;
  applyFilters: (filters: StructureFilterState) => void;
  applyReportConfig: (
    filterModel: { items: GridFilterItem[] },
    colSelection?: ColumnSelectionItem[],
    columnOrder?: Array<{ field: string }>
  ) => void;
  updateColumnSelection: (data: ColumnSelectionItem[]) => void;
  reorderColumns: (oldIndex: number, newIndex: number) => void;
  /** Restore the default view's own column layout (session, else built defaults)
   *  so returning to the default tab doesn't inherit a report's columns. */
  resetColumnsToDefault: () => void;
  /** Discard the saved session layout and rebuild from the built-in defaults
   *  — the column picker's "Reset to default". */
  restoreDefaultColumns: () => void;
  /** Set a single column's pixel width (from a header resize-drag). `seed` is
   *  the one-time snapshot of every column's on-screen width used to freeze
   *  not-yet-sized siblings so only the dragged column changes. */
  setColumnWidth: (key: string, width: number, seed?: Record<string, number>) => void;
  refetch: () => void;
}

const SESSION_PAGE_NAME = 'StructureList';

// Build the initial column defs, layering any browser-session layout
// (order/visibility/widths) on top of the defaults. A saved report still wins —
// it's applied afterwards via applyReportConfig.
const buildSessionColumns = (): ColumnDef[] =>
  applySessionColumnState(buildColumnDefs(), readSessionColumnState(SESSION_PAGE_NAME));

// Keep the picker (and the columns the BFF fetches) in sync with the restored
// columns: start from the default picker tree, then re-tick each field to match
// the session-applied columns' visibility.
const buildSessionColSelection = (): ColumnSelectionItem[] => {
  const visibleKeys = new Set(
    buildSessionColumns()
      .filter((c) => c.visible)
      .map((c) => c.key)
  );
  return buildInitialColumnSelection().map((group) => ({
    ...group,
    value: group.value.map((col) => ({ ...col, value: visibleKeys.has(col.field) })),
  }));
};

// Arriving from an import lands on that session's rows only; every other entry
// point starts on the list's default filters.
const buildInitialFilterModel = (importSessionId: string | null): { items: GridFilterItem[] } =>
  importSessionId
    ? { items: [{ field: 'importsessionId', operator: 'equals', value: importSessionId }] }
    : { items: getDefaultFilterItems() };

export const useStructureListData = (): StructureListDataReturn => {
  const [searchParams] = useSearchParams();
  const importSessionId = searchParams.get('importSessionId');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortModel, setSortModel] = useState<SortState>({ field: 'name', sort: 'asc' });
  const [filterModel, setFilterModel] = useState<{ items: GridFilterItem[] }>(() =>
    buildInitialFilterModel(importSessionId)
  );
  // Free-text search box value. Travels as the `multiSearchQuery` request param
  // (node name OR referenceNo1) rather than as a filterModel item.
  const [multiSearchQuery, setMultiSearchQuery] = useState('');
  const [columns, setColumns] = useState<ColumnDef[]>(buildSessionColumns);
  const [colSelectionData, setColSelectionData] =
    useState<ColumnSelectionItem[]>(buildSessionColSelection);
  const { resetColumnsToDefault, restoreDefaultColumns, setColumnWidth } = useSessionColumns({
    setColumns,
    setColSelectionData,
    buildColumns: buildSessionColumns,
    buildColSelection: buildSessionColSelection,
    sessionPageName: SESSION_PAGE_NAME,
  });
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const fetchStartRef = useRef<number | null>(null);
  const prevDataRef = useRef<Array<Record<string, unknown>>>([]);
  const prevTotalRef = useRef<number>(0);

  const structureTypesQuery = useGetStructureTypesByCategoryCode(StructureTypeCategory.Operational);
  const contractsQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Contract
  );

  // `contractsQuery` is enabled:false — fetch once on mount to populate the
  // Contract filter dropdown without waiting for a user interaction.
  // Mount-only kick for enabled:false queries. The query objects get a new
  // identity every render, so listing them would refetch per render — the
  // refetch-loop class this file is guarded against.
  useEffect(() => {
    contractsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationTypeOptions = useMemo<LabelValue[]>(
    () => buildLocationTypeOptions(structureTypesQuery.data),
    [structureTypesQuery.data]
  );

  const contractOptions = useMemo<LabelValue[]>(
    () => buildContractOptions(contractsQuery.data),
    [contractsQuery.data]
  );

  const queryOption = useMemo(
    () => ({
      filterModel,
      page,
      pageSize,
      sortModel: sortModel.sort ? [{ field: sortModel.field, sort: sortModel.sort }] : [],
      multiSearchQuery: multiSearchQuery || undefined,
    }),
    [filterModel, page, pageSize, sortModel, multiSearchQuery]
  );

  const query = useGetStructurePaginatedDataByType(
    StructureTypeCategory.Operational,
    queryOption,
    colSelectionData
  );

  // All manual refetch paths share one loop guard — see useRefetchLoopGuard.
  // The fetch timer starts inside the guarded callback so a call the guard
  // suppresses cannot record a stale start time.
  const guardedRefetch = useRefetchLoopGuard(
    useCallback(() => {
      fetchStartRef.current = performance.now();
      query.refetch();
    }, [query]),
    'Structures list (/structure/search)'
  );

  const { schedule: scheduleColumnRefetch, cancel: cancelColumnRefetch } =
    useColumnRefetchScheduler(guardedRefetch);

  // A pending column-triggered refetch would only duplicate the full
  // refetch about to run with the latest options — drop it.
  useEffect(() => {
    cancelColumnRefetch();
    guardedRefetch();
  }, [queryOption, cancelColumnRefetch, guardedRefetch]);

  useEffect(() => {
    if (!query.isFetching && fetchStartRef.current !== null) {
      setFetchDurationMs(performance.now() - fetchStartRef.current);
      fetchStartRef.current = null;
    }
  }, [query.isFetching]);

  const applyFilters = useCallback((filters: StructureFilterState) => {
    setMultiSearchQuery(filters.multiSearch || '');
    setFilterModel(buildFilterModel(filters));
    setPage(0);
  }, []);

  const updateColumnSelection = useCallback(
    (data: ColumnSelectionItem[]) => {
      const prevVisible = getVisibleFields(colSelectionData);
      const nextVisible = getVisibleFields(data);
      const hasAddedField = Array.from(nextVisible).some((f) => !prevVisible.has(f));

      setColSelectionData(data);
      setColumns((prev) => prev.map((c) => ({ ...c, visible: nextVisible.has(c.key) })));

      if (hasAddedField) scheduleColumnRefetch();
    },
    [colSelectionData, scheduleColumnRefetch]
  );

  const reorderColumns = useCallback((oldIndex: number, newIndex: number) => {
    setColumns((prev) => reorderByVisibleIndex(prev, oldIndex, newIndex));
  }, []);

  const applyReportConfig = useCallback(
    (
      reportFilterModel: { items: GridFilterItem[] },
      colSelection?: ColumnSelectionItem[],
      columnOrder?: Array<{ field: string }>
    ) => {
      // Saved reports don't carry the free-text search — clear it so the
      // applied query matches the (also-cleared) search box on report load.
      setMultiSearchQuery('');
      setFilterModel({
        ...reportFilterModel,
        items: sanitizeSavedReportFilterItems(reportFilterModel.items),
      });
      setPage(0);
      if (colSelection) updateColumnSelection(colSelection);
      // Reports don't store column widths — drop any user-dragged widths so the
      // report renders in its own (unfrozen) layout, then apply its saved order.
      setColumns((prev) =>
        dropColumnWidths(columnOrder ? applyColumnOrder(prev, columnOrder) : prev)
      );
    },
    [updateColumnSelection]
  );

  useEffect(() => {
    if (query.data?.data) {
      prevDataRef.current = query.data.data as unknown as Array<Record<string, unknown>>;
      prevTotalRef.current = query.data.total ?? 0;
    }
  }, [query.data]);

  const currentData =
    (query.data?.data as unknown as Array<Record<string, unknown>> | undefined) ??
    prevDataRef.current;
  const currentTotal = query.data?.total ?? prevTotalRef.current;

  return {
    data: currentData,
    isLoading: query.isLoading && prevDataRef.current.length === 0,
    isFetching: query.isFetching,
    page,
    pageSize,
    total: currentTotal,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData: colSelectionData,
    filterModel,
    multiSearchQuery,
    locationTypeOptions,
    contractOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    applyReportConfig,
    updateColumnSelection,
    reorderColumns,
    resetColumnsToDefault,
    restoreDefaultColumns,
    setColumnWidth,
    refetch: guardedRefetch,
  };
};
